import { handleResp, getDefaultHeaders, extendFetchOptions } from "./utils";

import type {
  GenericResponse,
  CommonResponse,
  WrappedCashifyFunction,
} from "./utils";
import type { MfaMethods, AuthenticationResponse } from "./auth-service";
import type { StandardObject } from "utils";

export interface AdminMfaAssetService {
  totpFirstGenerate: ReturnType<typeof totpFirstGenerate>;
  totpGenerate: ReturnType<typeof totpGenerate>;
  listAdminMfaAsset: ReturnType<typeof listAdminMfaAsset>;
  deleteAdminMfaAsset: ReturnType<typeof deleteAdminMfaAsset>;
  totpVerify: ReturnType<typeof totpVerify>;
  totpRegister: ReturnType<typeof totpRegister>;
  emailOtpInitiate: ReturnType<typeof emailOtpInitiate>;
  emailOtpVerify: ReturnType<typeof emailOtpVerify>;
}

interface BasicAdminMfaAssetRequest {
  adminId: number;
}

interface DeleteAdminMfaAssetRequest extends BasicAdminMfaAssetRequest {
  assetId: number;
}

interface TotpVerifyRequest extends BasicAdminMfaAssetRequest {
  passcode: string;
}

interface EmailOtpVerifyRequest extends BasicAdminMfaAssetRequest {
  otp: string;
}

export interface TotpRegisterFields {
  label?: string;
  assetCacheKey: string;
  passcode: string;
}
export interface TotpRegisterRequest
  extends BasicAdminMfaAssetRequest,
    TotpRegisterFields {}

export interface AdminMfaAssetDTO {
  id: number;
  method: MfaMethods;
  label: string;
  createTime: string;
  lastActivityTime: string;
}

export interface TemporaryMfaAsset {
  assetCacheKey: string;
  method: MfaMethods;
  asset: StandardObject;
  aliveSeconds: number;
}

export interface EmailOtpInitiateResult {
  emailAddress: string;
}

export const listAdminMfaAsset: WrappedCashifyFunction<
  BasicAdminMfaAssetRequest,
  GenericResponse<AdminMfaAssetDTO[]>
> = ({ urlPrefix, options }) => {
  return async ({ adminId }) => {
    let url = `${urlPrefix}/api/v1/admin/${adminId}/mfa-asset`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteAdminMfaAsset: WrappedCashifyFunction<
  DeleteAdminMfaAssetRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ adminId, assetId }) => {
    let url = `${urlPrefix}/api/v1/admin/${adminId}/mfa-asset/${assetId}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const totpFirstGenerate: WrappedCashifyFunction<
  BasicAdminMfaAssetRequest,
  GenericResponse<TemporaryMfaAsset>
> = ({ urlPrefix, options }) => {
  return async ({ adminId }) => {
    let url = `${urlPrefix}/api/v1/auth/admin/${adminId}/mfa-asset/totp/first-generate`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: {
          ...getDefaultHeaders(),
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const totpGenerate: WrappedCashifyFunction<
  BasicAdminMfaAssetRequest,
  GenericResponse<TemporaryMfaAsset>
> = ({ urlPrefix, options }) => {
  return async ({ adminId }) => {
    let url = `${urlPrefix}/api/v1/auth/admin/${adminId}/mfa-asset/totp/generate`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: {
          ...getDefaultHeaders(),
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const totpRegister: WrappedCashifyFunction<
  TotpRegisterRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ adminId, assetCacheKey, label, passcode }) => {
    let url = `${urlPrefix}/api/v1/auth/admin/${adminId}/mfa-asset/totp/register`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify({ label, assetCacheKey, passcode }),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const totpVerify: WrappedCashifyFunction<
  TotpVerifyRequest,
  GenericResponse<AuthenticationResponse>
> = ({ urlPrefix, options }) => {
  return async ({ adminId, passcode }) => {
    let url = `${urlPrefix}/api/v1/auth/admin/${adminId}/mfa-asset/totp/verify`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify({ passcode }),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const emailOtpInitiate: WrappedCashifyFunction<
  BasicAdminMfaAssetRequest,
  GenericResponse<EmailOtpInitiateResult>
> = ({ urlPrefix, options }) => {
  return async ({ adminId }) => {
    let url = `${urlPrefix}/api/v1/auth/admin/${adminId}/mfa-asset/email-otp/initiate`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: {
          ...getDefaultHeaders(),
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const emailOtpVerify: WrappedCashifyFunction<
  EmailOtpVerifyRequest,
  GenericResponse<AuthenticationResponse>
> = ({ urlPrefix, options }) => {
  return async ({ adminId, otp }) => {
    let url = `${urlPrefix}/api/v1/auth/admin/${adminId}/mfa-asset/email-otp/verify`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify({ otp }),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
